import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Target, ChevronRight, ArrowRight } from "lucide-react";
import { PageBody, PageHeader, EmptyState } from "@/components/Page";
import { BriefDialog } from "@/components/BriefDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { LabelHelp, PageTip } from "@/components/FieldHelp";
import { HELP } from "@/lib/fieldHelp";
import api from "@/lib/api";

export default function Offer() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product: "", features: "", proof: "", pricing_motion: "", desired_outcome: "", sales_strategy: "high-volume outbound" });

  const load = async () => { const { data } = await api.get("/offers"); setOffers(data); };
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async () => {
    if (!form.product) return toast.error("Product is required");
    const { data } = await api.post("/offers", form);
    setOpen(false);
    setForm({ product: "", features: "", proof: "", pricing_motion: "", desired_outcome: "", sales_strategy: "high-volume outbound" });
    await load();
    toast.success("Offer created");
    navigate(`/app/strategy?offer=${data.id}`);
  };

  return (
    <PageBody>
      <PageHeader eyebrow="Offer" title="Describe what you sell"
        subtitle="Each offer feeds the AI ICP brief, messaging angles, and every sequence. Create it once and reuse it everywhere."
        actions={
          <div className="flex items-center gap-2">
          <BriefDialog variant="outline" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="new-offer-btn">New offer</Button></DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Describe your offer</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><LabelHelp label="Product" help={HELP.offer_product} testid="help-offer-product" /><Input data-testid="offer-product" value={form.product} onChange={set("product")} placeholder="Unified sales control plane" className="mt-1.5" /></div>
                <div><LabelHelp label="Key features" help={HELP.offer_features} testid="help-offer-features" /><Textarea data-testid="offer-features" value={form.features} onChange={set("features")} placeholder="Sourcing, AI copilot, sequences, CRM" className="mt-1.5" /></div>
                <div><LabelHelp label="Proof / results" help={HELP.offer_proof} testid="help-offer-proof" /><Input value={form.proof} onChange={set("proof")} placeholder="Cuts cost per meeting ~40%" className="mt-1.5" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><LabelHelp label="Pricing motion" help={HELP.offer_pricing} testid="help-offer-pricing" /><Input value={form.pricing_motion} onChange={set("pricing_motion")} placeholder="Seat-based, 499/mo" className="mt-1.5" /></div>
                  <div><LabelHelp label="Desired outcome" help={HELP.offer_outcome} testid="help-offer-outcome" /><Input value={form.desired_outcome} onChange={set("desired_outcome")} placeholder="More qualified meetings" className="mt-1.5" /></div>
                </div>
                <div>
                  <LabelHelp label="Sales strategy" help={HELP.offer_strategy} testid="help-offer-strategy" />
                  <select value={form.sales_strategy} onChange={set("sales_strategy")} data-testid="offer-strategy"
                    className="mt-1.5 w-full min-h-[44px] md:h-10 rounded-md border border-input bg-background px-3 text-base md:text-sm">
                    <option value="high-volume outbound">High-volume outbound</option>
                    <option value="account-based">Account-based (ABM)</option>
                    <option value="public-sector procurement">Public-sector procurement</option>
                  </select>
                </div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="save-offer-btn" className="rounded-md bg-amber-500 text-black hover:bg-amber-400 min-h-[44px]">Create offer</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <PageTip id="offer">
        Create an offer, then open <span className="text-foreground font-medium">Strategy</span> to generate its ICP brief and three messaging angles.
      </PageTip>

      {offers.length === 0 ? (
        <EmptyState icon={Target} title="No offers yet" body="Create your first offer to unlock ICP and messaging strategy." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="offer-grid">
          {offers.map((o) => (
            <div key={o.id} data-testid={`offer-item-${o.id}`}
              className="rounded-xl border border-border/70 bg-card/50 p-5 hover:border-amber-500/50 transition-colors">
              <button onClick={() => navigate(`/app/strategy?offer=${o.id}`)} className="text-left w-full">
                <div className="flex items-center justify-between">
                  <span className="font-display font-semibold tracking-tight truncate">{o.product}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{o.features}</p>
                <div className="mt-4 flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">{o.sales_strategy}</Badge>
                  {o.icp ? <span className="text-[10px] font-mono text-emerald-500">ICP ready</span> : <span className="text-[10px] font-mono text-amber-500">needs strategy</span>}
                </div>
                <div className="mt-3 text-xs text-amber-500 flex items-center gap-1">Open strategy <ArrowRight className="h-3 w-3" /></div>
              </button>
              <BriefDialog sourceOffer={o} variant="outline" label="New sequence from this offer" className="w-full mt-3 text-xs" />
            </div>
          ))}
        </div>
      )}
    </PageBody>
  );
}
